<template>
  <div class="tc_wrap">
    <div v-for="(t, i) in items" :key="i" :class="['tool_card', t.kind]">
      <div class="tool_head" @click="t.open = !t.open">
        <span class="tool_kind">{{ toolLabel(t.kind) }}</span>
        <span class="tool_name">{{ t.name }}</span>
        <span class="tool_brief">{{ brief(t) }}</span>
        <i :class="t.open ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" class="tool_arrow" />
      </div>
      <div v-if="t.open" class="tool_detail">
        <!-- 历史 role=tool 记录携带 args，结果卡片补充展示调用参数 -->
        <template v-if="t.kind !== 'tool_call' && t.args">
          <div class="detail_label">参数</div>
          <pre v-if="pretty(t.args) !== null" class="tool_pre">{{ pretty(t.args) }}</pre>
          <pre v-else class="tool_pre">{{ t.args }}</pre>
        </template>
        <!-- knowledge.search 完整结果：命中片段列表 -->
        <template v-if="ksHits(t)">
          <div v-for="(h, j) in ksHits(t)" :key="j" class="ks_hit">
            <div class="ks_meta">
              <span class="ks_cit">{{ h.citation || '#' + (j + 1) }}</span>
              <span v-if="h.score">score {{ Number(h.score).toFixed(2) }}</span>
            </div>
            <div class="ks_text">{{ h.text }}</div>
          </div>
            <div v-if="ksParsed(t) && ksParsed(t).tokens_used" class="ks_tokens">tokens: {{ ksParsed(t).tokens_used }}</div>
        </template>
        <pre v-else-if="pretty(t.kind === 'tool_call' ? t.args : t.result) !== null" class="tool_pre">{{ pretty(t.kind === 'tool_call' ? t.args : t.result) }}</pre>
        <pre v-else-if="rawText(t)" class="tool_pre">{{ rawText(t) }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
// 工具调用卡片：流式 tool 事件与历史 role=tool 记录共用
// item 形状：{ kind:'tool_call'|'tool_result'|'tool_error', name, args, result, open }
export default {
  name: 'AiAgentToolCards',
  props: {
    items: { type: Array, default: () => [] }
  },
  methods: {
    toolLabel(kind) {
      return { tool_call: '调用', tool_result: '结果', tool_error: '出错' }[kind] || kind
    },
    parse(s) {
      if (!s) return null
      try {
        const v = JSON.parse(s)
        return (v && typeof v === 'object') ? v : null
      } catch (e) { return null }
    },
    // knowledge.search 完整结果（含 hits 数组；摘要事件只有 hit_count 不展开）
    ksParsed(t) {
      if (t.kind === 'tool_result' && t.name === 'knowledge.search') {
        const p = this.parse(t.result)
        if (p && Array.isArray(p.hits)) return p
      }
      return null
    },
    ksHits(t) {
      const p = this.ksParsed(t)
      return p && p.hits.length ? p.hits : null
    },
    // 可解析 JSON 统一 pretty 展示；解析失败返回 null 走原文分支
    pretty(s) {
      const p = this.parse(s)
      return p === null ? null : JSON.stringify(p, null, 2)
    },
    rawText(t) {
      return t.kind === 'tool_call' ? (t.args || '') : (t.result || '')
    },
    brief(t) {
      if (t.kind === 'tool_result' && t.name === 'knowledge.search') {
        const p = this.parse(t.result)
        if (p) {
          const n = Array.isArray(p.hits) ? p.hits.length : p.hit_count
          // 摘要事件（后端 eventSummaryOf）用 citation_labels（字符串数组），完整结果用 citations（对象数组）
          const cits = (p.citation_labels || p.citations || []).map(c => c.label || c).join(' ')
          return '命中 ' + (n || 0) + ' 条' + (cits ? ' · 引用 ' + cits : '') + (p.tokens_used ? ' · ' + p.tokens_used + ' tokens' : '')
        }
      }
      const s = t.kind === 'tool_call' ? t.args : t.result
      if (!s) return ''
      return s.length > 60 ? s.slice(0, 60) + '…' : s
    }
  }
}
</script>

<style lang="less" scoped>
.tc_wrap {
  .tool_card {
    margin-bottom: 8px;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    background: #fafafa;
    font-size: 12px;
    &:last-child { margin-bottom: 0; }
    .tool_head {
      display: flex;
      align-items: center;
      padding: 6px 10px;
      cursor: pointer;
      &:hover { background: #f5f7fa; }
      .tool_kind {
        flex-shrink: 0;
        margin-right: 8px;
        padding: 0 6px;
        border-radius: 3px;
        background: #ecf5ff;
        color: #409eff;
      }
      .tool_name {
        flex-shrink: 0;
        margin-right: 8px;
        color: #303133;
        font-weight: bold;
      }
      .tool_brief {
        flex: 1;
        min-width: 0;
        color: #909399;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .tool_arrow {
        flex-shrink: 0;
        margin-left: 6px;
        color: #c0c4cc;
      }
    }
    &.tool_error .tool_kind {
      background: #fef0f0;
      color: #f56c6c;
    }
    .tool_detail {
      padding: 6px 10px 8px;
      border-top: 1px dashed #ebeef5;
      .detail_label {
        margin-bottom: 2px;
        color: #c0c4cc;
        font-size: 11px;
      }
      .tool_pre {
        margin: 0;
        color: #606266;
        font-size: 12px;
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 240px;
        overflow-y: auto;
      }
      .ks_hit {
        padding: 4px 0;
        border-bottom: 1px dashed #ebeef5;
        &:last-of-type { border-bottom: none; }
        .ks_meta {
          display: flex;
          gap: 8px;
          color: #909399;
          .ks_cit {
            padding: 0 5px;
            border-radius: 3px;
            background: #f0f2f5;
            color: #409eff;
          }
        }
        .ks_text {
          margin-top: 2px;
          color: #606266;
          white-space: pre-wrap;
          word-break: break-word;
          max-height: 120px;
          overflow-y: auto;
        }
      }
      .ks_tokens {
        margin-top: 4px;
        color: #c0c4cc;
      }
    }
  }
}
</style>
